<template>
  <div
    class="select-none"
    :class="{ 'cursor-wait': addGraphLoading }"
    :style="{ minHeight: 'calc(100vh - 3.75rem - 3rem - 4px)' }"
  >
    <div class="flex flex-col">
      <div
        class="sticky top-0 z-10 px-6 py-4 border-b bg-pure-white border-slate-400"
      >
        <div class="flex items-center justify-between">
          <BlTextSubtitle2
            text="Add graph from target to report"
            class="text-slate-700"
          />
          <BlIcon
            icon="close"
            class="w-6 h-6"
            clickable
            @clicked="$emit('close')"
          />
        </div>
        <div class="mt-1">
          <BlTextBody2
            text="Drag and place the target into the report to insert its graph."
          />
        </div>
        <BlInputSearch
          class="w-full mt-4"
          :input="searchInput"
          @changed="onInputSearch"
        />
      </div>
      <div class="flex flex-col gap-4 px-6 py-4">
        <div class="flex items-center gap-2 pb-2 overflow-x-auto scrollbar-lg">
          <div
            v-for="opt in statusOptions"
            :key="opt.value"
            class="h-7.5 px-2 flex items-center flex-shrink-0 rounded-full cursor-pointer"
            :class="{
              'bg-light-purple-400': statusFilter === opt.value,
              'bg-slate-200 hover:bg-slate-300': statusFilter !== opt.value
            }"
            @click="statusFilter = opt.value"
          >
            <BlTextBody2
              :text="opt.label"
              :class="{ 'text-pure-white': statusFilter === opt.value }"
            />
          </div>
        </div>
        <div v-if="!filteredTargets.length">
          <div class="py-32 text-center">
            <BlTextBody2 text="Target(s) not found." />
          </div>
        </div>
        <div v-else class="flex flex-col gap-2">
          <div v-if="statusFilter === 'recommended'">
            <BlTextBody2 :text="filteredTargets.length" />
            <BlTextBody2
              text=" target(s) showed progress within the date range of "
            />
            <BlTextBody2 :text="displayDate({ date: report.start_date })" />
            <BlTextBody2 text=" to " />
            <BlTextBody2 :text="displayDate({ date: report.end_date })" />
            <BlTextBody2 text="." />
          </div>
          <div v-else>
            <BlTextBody2
              :text="`Showing all ${filteredTargets.length} target(s)`"
            />
          </div>
          <div
            v-for="target in filteredTargets"
            :key="target.id"
            class="flex items-center justify-between gap-2 px-2 py-2 border rounded bg-pure-white border-slate-400"
            :class="{ 'opacity-25': dragId === target.id }"
          >
            <div class="flex items-center gap-2 truncate" :title="target.name">
              <div
                class="cursor-grab"
                @mousedown="
                  $emit('drag-start', { e: $event, client_target: target })
                "
                @touchstart="
                  $emit('drag-start', { e: $event, client_target: target })
                "
                :data-id="target.id"
              >
                <BlIcon icon="drag" color="slate-8" class="w-5 h-5" />
              </div>
              <BlTextBody2 :text="target.name" class="truncate" />
            </div>
            <div class="flex items-center gap-2">
              <div
                class="flex"
                :class="{ 'opacity-0': target.type === 'cumulative' }"
              >
                <BlChip
                  :chip="
                    target.status === 'in_progress'
                      ? 'in_acquisition'
                      : target.status
                  "
                />
              </div>
              <div title="Add graph">
                <BlIcon
                  icon="plus"
                  color="slate-8"
                  class="w-4 h-4"
                  :clickable="!addGraphLoading"
                  @clicked="onAddGraph(target)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import { displayDate } from "@/plugins/helper.js";
import {
  PROMPT_COLORS,
  PROMPT_POINTS,
  PHASE_LINE_COLORS
} from "@/plugins/assets.js";

import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlInputSearch from "../../BlInputSearch.vue";
import BlIcon from "../../BlIcon.vue";
import BlChip from "../../BlChip.vue";

export default {
  name: "ReportAddGraph",
  components: {
    BlTextSubtitle2,
    BlTextBody2,
    BlInputSearch,
    BlIcon,
    BlChip
  },
  props: {
    dragId: { type: Number },
    recommendedTargets: { type: Array, default: () => [] }
    // @close
    // @change-focus-id
  },
  data() {
    return {
      addGraphLoading: false,
      //
      searchInput: {
        placeholder: "Search target by name",
        model: ""
      },
      statusFilter: "",
      statusOptions: [
        { value: "recommended", label: "Recommended" },
        { value: "", label: "All" },
        { value: "in_progress", label: "In acquisition" },
        { value: "mastered", label: "Mastered" },
        { value: "discontinued", label: "Discontinued" },
        { value: "paused", label: "Paused" },
        { value: "pending", label: "Pending" }
      ]
    };
  },
  computed: {
    ...mapGetters(["report", "targets"]),
    filteredTargets() {
      let arr = [...this.targets];

      const status = this.statusFilter;
      if (status === "recommended") {
        arr = [...this.recommendedTargets];
      } else if (status === "") {
        const cumulative = {
          id: "cumulative",
          name: "Cumulative graph",
          type: "cumulative"
        };
        arr = [cumulative, ...arr];
      } else {
        arr = arr.filter(i => i.status === status);
      }

      const search = this.searchInput.model;
      if (search) {
        const key = search.toLowerCase();
        arr = arr.filter(i => {
          const name = i.name.toLowerCase();
          return name.includes(key);
        });
      }
      return arr;
    }
  },
  watch: {},
  mounted() {},
  methods: {
    ...mapActions(["getTarget", "getGraphDataSection", "createSection"]),
    displayDate,
    onInputSearch(payload) {
      payload.input.model = payload.value;
    },
    //
    async onAddGraph(target) {
      if (this.addGraphLoading) return;

      this.addGraphLoading = true;

      let kind = "graph";
      /**
       kind
       * graph -> frequency / other
       * graph_percentage -> percentage
       * graph_pir -> pir
       * graph_duration -> duration
       * graph_prompting -> prompting
       * cumulative_graph_line
       * cumulative_graph_stacked
       * graph_sbt -> sbt
      */
      if (target.type === "Target::Percentage") {
        kind = "graph_percentage";
      }
      if (target.type === "Target::Pir") {
        kind = "graph_pir";
      }
      if (target.type === "Target::Duration") {
        kind = "graph_duration";
      }
      if (target.type === "Target::Prompting") {
        kind = "graph_prompting";
      }
      if (target.type === "cumulative") {
        kind = "cumulative_graph_line";
      }
      if (target.type === "Target::Sbt") {
        kind = "graph_sbt";
      }

      const {
        graphData,
        prompts,
        targetTasks,
        targetProblemBehaviors
      } = await this.fetchGraphDataSection(target);
      const generated = this.generateCollection({
        target,
        graphData,
        prompts,
        targetTasks,
        targetProblemBehaviors
      });
      const stringify = JSON.stringify(generated);

      let htmlText = "";
      if (target.type === "cumulative") {
        htmlText = `<p class="ql-align-center"><strong>${target.name}</strong></p>`;
      } else {
        const statusOptions = {
          mastered: "Mastered",
          in_progress: "In acquisition",
          paused: "Paused",
          discontinued: "Discontinued",
          pending: "Pending"
        };
        const targetStatus = statusOptions[target.status];
        htmlText = `<p class="ql-align-center"><strong>${target.name}</strong></p><p class="ql-align-center">Current status: ${targetStatus}</p><p class="ql-align-center">Curriculum: ${target.curriculum_name}</p>`;
      }

      const textParams = {
        slug: this.report.slug,
        section: { data: htmlText, kind: "text" }
      };
      const { success: textSuccess } = await this.createSection(textParams);

      const graphParams = {
        slug: this.report.slug,
        section: { data: stringify, kind }
      };
      const { success: graphSuccess, data } = await this.createSection(
        graphParams
      );

      this.addGraphLoading = false;
      if (textSuccess && graphSuccess) {
        setTimeout(() => {
          this.$emit("change-focus-id", data.id);
        }, 300);
      }
    },
    async fetchGraphDataSection(target) {
      const id =
        target.type === "cumulative" ? this.report.client_id : target.id;
      const type = target.type === "cumulative" ? "cumulative" : "target";

      let prompts = [];
      let targetTasks = [];
      let targetProblemBehaviors = [];
      if (target.type === "Target::Prompting") {
        const { data } = await this.getTarget({ id });
        prompts = data.prompts;
      }
      if (target.type === "Target::Sbt") {
        const { data } = await this.getTarget({ id });
        targetTasks = data.target_tasks;
        targetProblemBehaviors = data.target_problem_behaviors;
      }

      const start = this.displayDate({
        date: this.report.start_date,
        format: "YYYY-MM-DD"
      });
      const end = this.displayDate({
        date: this.report.end_date,
        format: "YYYY-MM-DD"
      });
      let params = `?start_date=${start}&end_date=${end}`;
      if (target.type === "cumulative") {
        const graphType = "line";
        params += `&view=graph&group_by=month`;
        params += `&graph_type=${graphType}`;
      }
      if (target.type === "Target::Prompting") {
        params += `&progress_by=frequency`;
      }

      let graphData = { labels: [], datasets: [], recorded_by: [] };

      const res = await this.getGraphDataSection({ id, type, params });
      if (res) graphData = res;

      return { graphData, prompts, targetTasks, targetProblemBehaviors };
    },
    generateCollection({
      target,
      graphData,
      prompts,
      targetTasks,
      targetProblemBehaviors
    }) {
      const {
        labels = [],
        datasets = [],
        recorded_by: recordedBy = []
      } = graphData;
      const isCumulative = target.type === "cumulative";
      const isStacked = false;
      const isPrompting = target.type === "Target::Prompting";
      const isSbt = target.type === "Target::Sbt";
      const progressBy = isPrompting ? "frequency" : "";

      // setup title
      let newTitle = "";
      if (target.name) {
        newTitle = target.name;
      }
      if (isCumulative) {
        newTitle = "Cumulative graph";
      }

      // setup goal number
      let newGoal = null;
      if (target.goal) {
        newGoal = target.goal;
        if (
          progressBy === "percentage" ||
          (progressBy === "frequency" && target.prompting_format === "custom")
        ) {
          newGoal = null;
        }
      }

      // setup prompts
      let newPrompts = [];
      let newTasks = [];
      let newProblemBehaviors = [];
      if (isPrompting) {
        newPrompts = prompts.map(i => {
          return {
            id: i.id,
            name: i.name,
            abbreviation: i.abbreviation,
            color: i.color,
            shape: i.shape,
            position: i.position,
            is_show: true
          };
        });
      }
      if (isSbt) {
        newTasks = targetTasks.map(i => {
          return {
            id: i.id,
            code: i.code,
            code_definition: i.code_definition,
            color: i.color,
            is_show: true
          };
        });
        newProblemBehaviors = targetProblemBehaviors.map(i => {
          return {
            id: i.id,
            code: i.code,
            code_definition: i.code_definition,
            color: i.color,
            is_show: true
          };
        });
      }

      // setup labels and hasPhaseLine value
      let newLabels = [];
      const phaseLineIndexes = [];
      labels.forEach((i, idx) => {
        if (!i) {
          newLabels[idx] = "";
          phaseLineIndexes.push(idx);
        } else {
          const display = isCumulative ? "month" : "date";
          let res = "";
          if (display === "date") {
            const arr = i.split("-").reverse();
            res = [`${arr.at(0)}/${arr.at(1)}`, arr.at(2)];
          }
          if (display === "month") {
            res = [moment(i).format("MMM"), moment(i).format("YYYY")];
          }
          newLabels[idx] = res;
        }
      });

      let newDatasets = [];
      if (datasets.length) {
        if (isCumulative && isStacked) {
          // setup core datasets property [stacked]
          datasets.forEach((i, idx) => {
            const bg =
              i.color +
              Math.round(Math.min(Math.max(0.75), 1) * 255)
                .toString(16)
                .toUpperCase();
            newDatasets[idx] = {
              label: i.label,
              data: i.data,
              backgroundColor: bg,
              borderColor: "#E4E4E4",
              hoverBorderColor: "#9A8AC2",
              borderWidth: 0,
              hoverBorderWidth: 2,
              borderRadius: 15
            };
          });
        } else {
          // setup core datasets property [line]
          if (isPrompting && progressBy !== "final_score") {
            // promting with frequency and percentage progress_by
            // prepare prompting property
            newDatasets = newPrompts.map(i => {
              return {
                promptId: i.id,
                label: i.name,
                data: [],
                // line style
                backgroundColor: PROMPT_COLORS[i.color].primaryColor,
                borderColor: PROMPT_COLORS[i.color].primaryColor,
                // point style
                pointStyle: PROMPT_POINTS[i.shape].pointStyle,
                pointBackgroundColor: PROMPT_COLORS[i.color].primaryColor,
                pointBorderColor: PROMPT_COLORS[i.color].secondaryColor,
                pointRadius: 4,
                hoverRadius: 4,
                fill: false,
                tension: 0.1
              };
            });
            // input prompting data value
            datasets[0].data.forEach(entry => {
              newPrompts.forEach((prompt, idx) => {
                let val = null;
                if (prompt.is_show && entry) {
                  val = entry[prompt.id];
                }
                newDatasets[idx].data.push(val);
              });
            });
            // add goal for prompting with frequency present
            if (
              progressBy === "frequency" &&
              target.prompting_format === "classic"
            ) {
              const goalDataset = {
                label: datasets[1].label,
                data: datasets[1].data,
                backgroundColor: "#71C115",
                borderColor: "#71C115",
                pointRadius: 0,
                fill: false,
                tension: 0.1
              };
              newDatasets.push(goalDataset);
            }
          } else {
            // includes prompting with final_score progress_by
            newDatasets = datasets.map(i => {
              const color = i.color ? i.color : "primary"; // set 'primary' for default color
              const propertyColor =
                i.label === "Goal" ? "#71C115" : PHASE_LINE_COLORS[color];
              return {
                label: i.label,
                data: i.data,
                backgroundColor: propertyColor,
                borderColor: propertyColor,
                pointRadius: i.label === "Goal" ? 0 : 4,
                hoverRadius: 4,
                fill: false,
                tension: 0.1
              };
            });
          }
        }
      }

      if (
        datasets &&
        datasets.data &&
        datasets.data.tasks &&
        datasets.data.problem_behaviors
      ) {
        newDatasets = [
          ...newTasks.map(i => ({
            taskId: i.id,
            type: "line",
            label: i.code,
            data: [],
            backgroundColor: i.color,
            borderColor: i.color,
            pointBackgroundColor: i.color,
            pointBorderColor: i.color,
            pointRadius: 4,
            hoverRadius: 4,
            fill: false,
            tension: 0.1
          })),
          ...newProblemBehaviors.map(i => ({
            type: "bar",
            behaviorId: i.id,
            label: i.code,
            data: [],
            backgroundColor: i.color,
            borderColor: i.color,
            yAxisID: "y-axis-bar",
            barPercentage: 10,
            barThickness: 100,
            maxBarThickness: 100,
            minBarLength: 10
          }))
        ];

        // Proses data untuk defaultTasks
        datasets.data.tasks.forEach(entry => {
          newTasks.forEach((task, idx) => {
            let val = null;
            if (task.is_show && entry && entry[task.id]) {
              val = entry[task.id].score;
            }
            newDatasets[idx].data.push(val);
          });
        });

        // Proses data untuk problem behaviors
        datasets.data.problem_behaviors.forEach(entry => {
          newProblemBehaviors.forEach((behavior, idx) => {
            let val = null;
            if (behavior.is_show && entry && entry[behavior.id]) {
              val = entry[behavior.id].score;
            }
            newDatasets[newTasks.length + idx].data.push(val);
          });
        });
      }

      // generate trendline and average line
      let isTargetHasTrendline = true;
      if (isCumulative) isTargetHasTrendline = false;
      if (isPrompting) {
        if (progressBy === "final_score") isTargetHasTrendline = true;
        else {
          const isShowPrompts = [...newPrompts].filter(i => i.is_show);
          if (isShowPrompts.length === 1) isTargetHasTrendline = true;
          else isTargetHasTrendline = false;
        }
      }
      if (isSbt) isTargetHasTrendline = false;

      let isTargetHasAverage = true;
      if (isCumulative) isTargetHasAverage = false;
      if (isPrompting || isSbt) isTargetHasAverage = false;
      if (phaseLineIndexes.length > 0) isTargetHasAverage = false;

      const trendlines = [];
      const averageLines = [];
      if (isTargetHasTrendline) {
        newDatasets.forEach(dataset => {
          // only generate non-goal label
          if (dataset.label !== "Goal") {
            // trendline
            const arr = { a: [], b: [], c: [] };
            let firstAttemp = false;
            let endOfFirtsAttemp = false;
            dataset.data.forEach((val, index) => {
              /*
                    if val === null && index === 0  => first=true
                    if first
                      if      val === null && !endA => a.push
                      elseif  val !== null          => endA=true; b.push
                      elseif  val === null          => c.push
                    else
                      if      val !== null          => b.push
                      elseif  val === null          => c.push
                    end
                    */
              if (val === null && index === 0) {
                firstAttemp = true;
              }
              if (firstAttemp) {
                if (val === null && !endOfFirtsAttemp) {
                  arr.a.push(val);
                } else if (val !== null) {
                  endOfFirtsAttemp = true;
                  arr.b.push(val);
                } else if (val === null) arr.c.push(val);
              } else {
                if (val !== null) arr.b.push(val);
                else if (val === null) arr.c.push(val);
              }
            });
            // start generate trendline
            const idx = arr.b.length - 1;
            const dt = [
              arr.b[0],
              ...this.interpolate(arr.b[0], arr.b[idx], idx - 1),
              arr.b[idx]
            ];
            // remove the trendline if there are only 2 points
            if (dt.length === 2) {
              dt.pop();
            }
            let res = [...arr.a, ...dt, ...arr.c];
            const trendline = {
              key: "trendline",
              label: "Trendline",
              data: res,
              borderColor: "#6750A4",
              backgroundColor: "rgba(0, 0, 0, 0)",
              borderWidth: 2,
              borderDash: [12],
              pointRadius: 0
            };
            trendlines.push(trendline);
          }
        });
      }
      if (isTargetHasAverage) {
        newDatasets.forEach(dataset => {
          // only generate non-goal label
          if (dataset.label !== "Goal") {
            // average line
            let totalScore = 0;
            let totalCount = 0;
            dataset.data.forEach(val => {
              if (val !== null) {
                totalScore += val;
                totalCount += 1;
              }
            });
            // start generate average
            const average = Number((totalScore / totalCount).toFixed(0));
            const resAverage = Array(totalCount).fill(average);
            const averageLine = {
              key: "average",
              label: "Average",
              data: resAverage,
              borderColor: "#71C115",
              backgroundColor: "rgba(0, 0, 0, 0)",
              borderWidth: 2,
              borderDash: [12],
              pointRadius: 2
            };
            averageLines.push(averageLine);
          }
        });
      }
      trendlines.forEach(i => {
        newDatasets.push(i);
      });
      averageLines.forEach(i => {
        newDatasets.push(i);
      });

      // generate legends -- empty

      const startDate = this.displayDate({
        date: this.report.start_date,
        format: "YYYY-MM-DD"
      });
      const endDate = this.displayDate({
        date: this.report.end_date,
        format: "YYYY-MM-DD"
      });
      const masteredDate = target.mastered_date;

      return {
        title: newTitle,
        targetId: target.id,
        goal: newGoal,
        hasPhaseLine: phaseLineIndexes.length > 0,
        prompts: newPrompts,
        tasks: newTasks,
        problemBehaviors: newProblemBehaviors,
        labels: newLabels,
        datasets: newDatasets,
        recordedBy: recordedBy,
        startDate: startDate,
        endDate: endDate,
        masteredDate: masteredDate,
        promptingProgressBy: progressBy
      };
    },
    interpolate(first, last, count) {
      const interpolatedPoints = [];

      // Calculate the difference between start and end coordinates
      const deltaX = (last - first) / (count + 1);

      // Interpolate the points
      for (let i = 1; i <= count; i++) {
        const interpolatedX = Number(first + deltaX * i);
        interpolatedPoints.push(interpolatedX);
      }

      return interpolatedPoints;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/reports/helper/AddGraph.vue